import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Sparkles,
  ArrowRight,
  Youtube,
  Twitch,
  Gamepad2,
  Music2,
  Camera,
  Scissors,
  Zap,
  Users,
  Wallet,
  MessageSquare,
  Star,
  Clock,
  TrendingUp,
  Award,
  Heart,
  Layers,
  Check,
  X,
  ChevronDown,
  Play,
} from "lucide-react";
import TheNav from "../Components/navbar";
import Footer from "../Components/footer";
import CreatorProofSection from "../Components/CreatorProofSection";
import SEO, { organizationSchema, websiteSchema } from "../Components/SEO";
import { trackEvent } from "../utils/analytics";
import EditorSpotlightCard, {
  SpotlightEmptyState,
} from "../Components/EditorSpotlightCard";
import { getHomepageSpotlightEditors } from "../utils/editorSpotlight";
import { listPublicProfiles } from "../services/publicProfiles";
import a1 from '../images/anteos1.jpeg';

const PURPLE = "#9146FF";
const PURPLE_DEEP = "#7a3ae7";
const GREEN = "#84ff00";

export default function Main() {
  return (
    <>
      <SEO
        title="Kre8 Content | Twitch Editors and Clippers"
        description="Find Twitch editors and clippers for highlights, clipping campaigns, YouTube gaming videos, Shorts, TikToks, Reels, and creator growth content."
        canonicalPath="/home"
        noindex
        structuredData={[organizationSchema, websiteSchema]}
      />
      <div className="mobile-safe min-h-screen bg-[#07060d] text-zinc-200 overflow-x-hidden font-sans antialiased selection:bg-[#9146FF]/40">
        <TheNav />
        <Hero />
        <HomepageSpotlight />
        <Stats />
        <CreatorProofSection />
        <WhoFor />
        <Marketplace />
        <ContentTypes />
        <HowItWorks />
        <Rewards />
        <Compare />
        <FAQ />
        <FinalCTA />
        <Footer />
      </div>
    </>
  );
}

function HomepageSpotlight() {
  const [spotlightEditors, setSpotlightEditors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpotlightEditors = async () => {
      try {
        const editors = await listPublicProfiles({ role: "Editor" });

        setSpotlightEditors(getHomepageSpotlightEditors(editors, 3));
      } catch (error) {
        console.error("Failed to load homepage spotlight editors:", error);
        setSpotlightEditors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSpotlightEditors();
  }, []);

  return (
    <section className="pb-8 sm:pb-12">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: GREEN }}
            >
              Editor Elite
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Homepage Spotlight
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-zinc-400 sm:text-base">
              Spotlight editors get premium homepage placement, with their main game specialization front and center for creators browsing Kre8.
            </p>
          </div>
          <Link
            to="/pricing"
            className="hidden rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white transition hover:border-white/20 hover:bg-white/[0.08] sm:inline-flex"
          >
            View pricing
          </Link>
        </div>

        {loading ? (
          <div className="grid gap-4 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="h-56 animate-pulse rounded-3xl border border-white/10 bg-white/[0.03]"
              />
            ))}
          </div>
        ) : spotlightEditors.length > 0 ? (
          <>
            <div className="grid gap-4 lg:grid-cols-3">
              {spotlightEditors.map((editor) => (
                <EditorSpotlightCard
                  key={editor.id}
                  editor={editor}
                />
              ))}
            </div>
            <p className="mt-5 text-center text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Spotlight visibility rotates daily across active editors, with higher featured priority still favored first.
            </p>
          </>
        ) : (
          <SpotlightEmptyState copy="No homepage spotlight editors are active yet. Give an editor an active Editor Elite subscription or set visibilityBoosts.homepageSpotlightUntil to test this section." />
        )}
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="relative pt-12 pb-16 sm:pt-24 sm:pb-32">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-80"
        style={{
          background: `radial-gradient(60% 50% at 20% 20%, ${PURPLE}33, transparent 60%), radial-gradient(50% 50% at 85% 30%, ${GREEN}1a, transparent 60%), radial-gradient(40% 40% at 50% 90%, ${PURPLE_DEEP}33, transparent 60%)`,
        }}
      />

      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
        }}
      />

      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-6">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border"
            style={{
              borderColor: `${GREEN}55`,
              background: `${GREEN}10`,
              color: GREEN,
              boxShadow: `0 0 24px ${GREEN}22`,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: GREEN,
                boxShadow: `0 0 8px ${GREEN}`,
              }}
            />
            Built for Twitch streamers, editors, and clippers
          </div>

          <h1 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl lg:leading-[1.05]">
            Turn Twitch Streams Into{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(120deg, ${GREEN}, #ffffff 50%, ${PURPLE})`,
              }}
            >
              Growth Content
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-400 sm:mt-6 sm:text-lg">
            Find editors for custom videos and clippers for campaign driven
            short form content across TikTok, Shorts, and Reels.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/gaming-video-editors"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 sm:w-auto sm:text-base"
              style={{
                background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})`,
                boxShadow: `0 10px 40px ${PURPLE}55, inset 0 1px 0 rgba(255,255,255,.15)`,
              }}
              onClick={() =>
                trackEvent("find_editor_click", {
                  location: "hero",
                })
              }
            >
              Browse Editors and Clippers
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </Link>

            <Link
              to="/signup"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-black transition-all hover:-translate-y-0.5 sm:w-auto sm:text-base"
              style={{
                background: GREEN,
                boxShadow: `0 10px 40px ${GREEN}40`,
              }}
              onClick={() =>
                trackEvent("signup_click", {
                  location: "hero",
                })
              }
            >
              Join Kre8
            </Link>
            <Link
              to="/clipping-campaigns"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:border-white/30 sm:w-auto sm:text-base"
              style={{
                background: "rgba(255,255,255,0.03)",
              }}
            >
              Clipping Campaigns
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs text-zinc-500 sm:gap-5">
            <div className="flex items-center gap-2">
              <Check
                className="w-3.5 h-3.5"
                style={{ color: GREEN }}
              />
              No hidden fees
            </div>

            <div className="flex items-center gap-2">
              <Check
                className="w-3.5 h-3.5"
                style={{ color: GREEN }}
              />
              Direct collaboration
            </div>

            <div className="flex items-center gap-2">
              <Check
                className="w-3.5 h-3.5"
                style={{ color: GREEN }}
              />
              Long term partnerships
            </div>
          </div>
        </div>

        <div className="relative lg:col-span-6">
          <HeroCollage />
        </div>
      </div>
    </section>
  );
}

function HeroCollage() {
  return (
    <div className="relative mx-auto h-[360px] max-w-[34rem] sm:h-[520px] lg:h-[560px]">
      
      <FloatingCard
        className="absolute left-0 top-0 z-20 w-[72%] sm:w-[68%]"
        delay="0s"
      >
        <Link to="/editor/anteos1editor">
        <div className="flex items-center gap-3">
          <img
            className="w-12 h-12 rounded-xl"
            src={a1}
            style={{
              background: `linear-gradient(135deg, ${PURPLE}, ${GREEN})`,
            }}
          />

          <div className="flex-1">
            <div className="text-sm font-semibold text-white">
              @anteos1editor
            </div>

            <div className="text-xs text-zinc-400">
              Twitch Highlights · Gaming
            </div>
          </div>

          <span
            className="text-[10px] px-2 py-1 rounded-full font-semibold"
            style={{
              background: `${GREEN}20`,
              color: GREEN,
            }}
          >
            ONLINE
          </span>
        </div>
</Link>

        <div className="mt-4 flex items-center gap-2 text-xs">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className="w-3.5 h-3.5 fill-current"
              style={{ color: GREEN }}
            />
          ))}

          <span className="text-zinc-400">4.9 · 23 jobs</span>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <Thumb
            tint={PURPLE}
            icon={<Twitch className="w-4 h-4" />}
          />

          <Thumb
            tint={GREEN}
            icon={<Youtube className="w-4 h-4" />}
          />

          <Thumb
            tint="#ec4899"
            icon={<Gamepad2 className="w-4 h-4" />}
          />
        </div>
      </FloatingCard>

      <FloatingCard
        className="absolute right-0 top-[18%] z-30 w-[56%] sm:w-[52%]"
        delay="0.4s"
      >
        <div className="flex items-center justify-between">
          <div className="text-xs text-zinc-400">This week</div>

          <TrendingUp
            className="w-4 h-4"
            style={{ color: GREEN }}
          />
        </div>

        <div className="mt-2 text-2xl font-bold text-white">+128%</div>

        <div className="text-xs text-zinc-500">
          Upload consistency
        </div>

        <div className="mt-3 h-10 flex items-end gap-1">
          {[30, 45, 35, 60, 55, 78, 90].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm"
              style={{
                height: `${h}%`,
                background: `linear-gradient(180deg, ${GREEN}, ${PURPLE})`,
                opacity: 0.6 + i * 0.05,
              }}
            />
          ))}
        </div>
      </FloatingCard>

      <FloatingCard
        className="absolute bottom-[12%] left-[2%] z-30 w-[66%] sm:left-[8%] sm:w-[58%]"
        delay="0.7s"
      >
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare
            className="w-4 h-4"
            style={{ color: PURPLE }}
          />

          <div className="text-xs font-medium text-zinc-300">
            Direct message
          </div>
        </div>

        <div className="space-y-2 text-xs">
          <div className="bg-white/5 rounded-xl px-3 py-2 max-w-[80%]">
            Cut at 0:42 and match the beat drop?
          </div>

          <div
            className="ml-auto rounded-xl px-3 py-2 max-w-[80%] text-black font-medium"
            style={{ background: GREEN }}
          >
            On it, version 2 in 2hrs 
          </div>
        </div>
      </FloatingCard>

      <FloatingCard
        className="absolute bottom-0 right-[1%] z-20 w-[52%] sm:right-[4%] sm:w-[46%]"
        delay="1s"
      >
        <div className="flex items-center justify-between">
          <Wallet
            className="w-4 h-4"
            style={{ color: GREEN }}
          />

          <span className="text-[10px] text-zinc-500">Payout</span>
        </div>

        <div className="mt-2 text-xl font-bold text-white">
          $2,480
        </div>

        <div className="text-xs text-zinc-500">
          + 1,200 reward pts
        </div>

        <div
          className="mt-3 h-1.5 rounded-full overflow-hidden"
          style={{
            background: "rgba(255,255,255,0.06)",
          }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: "72%",
              background: `linear-gradient(90deg, ${PURPLE}, ${GREEN})`,
            }}
          />
        </div>
      </FloatingCard>
    </div>
  );
}

function Thumb({ tint, icon }) {
  return (
    <div
      className="aspect-video rounded-lg flex items-center justify-center text-white"
      style={{
        background: `linear-gradient(135deg, ${tint}55, ${tint}11)`,
        border: `1px solid ${tint}40`,
      }}
    >
      {icon}
    </div>
  );
}

function FloatingCard({
  children,
  className = "",
  delay = "0s",
}) {
  return (
    <div
      className={`rounded-2xl border border-white/10 p-3 backdrop-blur-xl sm:p-4 ${className}`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04), 0 0 40px ${PURPLE}15`,
        animation: `floatY 6s ease-in-out ${delay} infinite`,
      }}
    >
      <style>{`
        @keyframes floatY {
          0%,100% { transform: translateY(0) }
          50% { transform: translateY(-10px) }
        }
      `}</style>

      {children}
    </div>
  );
}

function Stats() {
  const stats = [
    { v: "10 minute", l: "Support response time" },
    { v: "10", l: "Categories" },
    { v: "100%", l: "User satisfaction" },
    { v: "<78hr", l: "Avg. turnaround" },
  ];

  return (
    <section className="py-16 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div
            key={s.l}
            className="rounded-2xl p-6 backdrop-blur-xl border border-white/10"
            style={{
              background:
                "linear-gradient(135deg, rgba(145,70,255,0.08), rgba(132,255,0,0.03))",
            }}
          >
            <div
              className="text-3xl sm:text-4xl font-bold bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(120deg, ${GREEN}, ${PURPLE})`,
              }}
            >
              {s.v}
            </div>

            <div className="mt-1 text-sm text-zinc-400">
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhoFor() {
  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <SectionHeader
          eyebrow="Two sides. One network."
          title="Built for creators, editors, and clippers"
          sub="Creators get content help. Editors build custom work. Clippers get ready for campaigns around short form growth."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <BenefitCard
            tint={PURPLE}
            icon={<Camera className="w-5 h-5" />}
            title="For Creators"
            cta={{
              label: "Hire An Editor",
              to: "/gaming-video-editors",
            }}
            items={[
              {
                i: <Users className="w-4 h-4" />,
                t: "Hire niche editors who get your content",
              },
              {
                i: <Zap className="w-4 h-4" />,
                t: "Upload faster, post more consistently",
              },
              {
                i: <MessageSquare className="w-4 h-4" />,
                t: "Direct communication without random DMs",
              },
              {
                i: <Layers className="w-4 h-4" />,
                t: "Scale your entire content pipeline",
              },
              {
                i: <Heart className="w-4 h-4" />,
                t: "Build long term creative relationships",
              },
            ]}
          />

          <BenefitCard
            tint={GREEN}
            icon={<Scissors className="w-5 h-5" />}
            title="For Editors and Clippers"
            cta={{
              label: "Join Early",
              to: "/for-clippers",
            }}
            items={[
              {
                i: <TrendingUp className="w-4 h-4" />,
                t: "Get ready for campaign based clipping",
              },
              {
                i: <Wallet className="w-4 h-4" />,
                t: "Build long term creator relationships",
              },
              {
                i: <Award className="w-4 h-4" />,
                t: "Showcase your portfolio to real creators",
              },
              {
                i: <Users className="w-4 h-4" />,
                t: "Work directly with creators you respect",
              },
              {
                i: <Star className="w-4 h-4" />,
                t: "Earn rewards, payouts, and exposure",
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function BenefitCard({
  tint,
  icon,
  title,
  items,
  cta,
}) {
  return (
    <div
      className="group relative overflow-hidden rounded-3xl border border-white/10 p-5 backdrop-blur-xl transition hover:-translate-y-1 sm:p-8"
      style={{
        background: `linear-gradient(160deg, ${tint}18, rgba(255,255,255,0.02) 60%)`,
        boxShadow: `0 20px 60px rgba(0,0,0,0.4), 0 0 60px ${tint}15`,
      }}
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-40 group-hover:opacity-60 transition"
        style={{ background: tint }}
      />

      <div className="relative">
        <div
          className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-5"
          style={{
            background: `${tint}25`,
            color: tint,
            border: `1px solid ${tint}55`,
          }}
        >
          {icon}
        </div>

        <h3 className="text-2xl font-bold text-white">{title}</h3>

        <ul className="mt-6 space-y-3">
          {items.map((it, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-sm text-zinc-300"
            >
              <span
                className="mt-0.5 inline-flex w-7 h-7 items-center justify-center rounded-lg shrink-0"
                style={{
                  background: `${tint}18`,
                  color: tint,
                }}
              >
                {it.i}
              </span>

              {it.t}
            </li>
          ))}
        </ul>

        <Link
          to={cta.to}
          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold transition group/btn"
          style={{ color: tint }}
        >
          {cta.label}

          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition" />
        </Link>
      </div>
    </div>
  );
}

function Marketplace() {
  const editors = [
    {
      name: "a1",
      niche: "Stream Clips",
      tag: "Twitch",
      rate: "Twitch VODs",
      tint: "#ec4899",
    }, {
      name: "anteos1",
      niche: "Twitch Highlights",
      tag: "Gaming",
      rate: "League of Legends",
      tint: PURPLE,
    },
    {
      name: "planty480",
      niche: "YouTube Gaming",
      tag: "Gaming",
      rate: "League of Legends",
      tint: GREEN,
    },
  ];

  return (
    <section className="py-24 relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="The marketplace"
          title="Browse editors and clippers built for Twitch content"
          sub="Editors handle custom videos. Clippers join campaigns that turn stream moments into TikToks, Shorts, Reels, and highlights."
        />

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {editors.map((e) => (
            <EditorCard key={e.name} {...e} />
          ))}
        </div>

        <div className="mt-10 flex flex-col items-stretch justify-center gap-3 text-center sm:flex-row sm:items-center">
          <Link
            to="/gaming-video-editors"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 font-semibold text-white transition hover:border-white/30"
            style={{
              background: "rgba(255,255,255,0.03)",
            }}
            onClick={() =>
                trackEvent("find_editor_click", {
                  location: "editorCard",
                })
              }
          >
            View Twitch editors
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/clipping-campaigns"
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-black transition hover:-translate-y-0.5"
            style={{
              background: GREEN,
              boxShadow: `0 10px 40px ${GREEN}30`,
            }}
          >
            See clipping campaigns
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function EditorCard({
  name,
  niche,
  tag,
  rate,
  tint,
}) {
  return (
    <div
      className="group rounded-2xl border border-white/10 overflow-hidden transition hover:-translate-y-1"
      style={{
        background:
          "linear-gradient(160deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
      }}
    >
      <div
        className="aspect-[16/9] relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${tint}66, #0a0a14 70%)`,
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
            maskImage:
              "radial-gradient(ellipse at 50% 50%, black, transparent 70%)",
          }}
        />

        <div
          className="absolute top-3 left-3 text-[10px] px-2 py-1 rounded-full font-semibold backdrop-blur"
          style={{
            background: `${tint}30`,
            color: "#fff",
            border: `1px solid ${tint}55`,
          }}
        >
          {tag.toUpperCase()}
        </div>

        <Play className="absolute bottom-3 right-3 w-8 h-8 text-white/80 group-hover:scale-110 transition" />
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full"
              style={{
                background: `linear-gradient(135deg, ${tint}, ${PURPLE})`,
              }}
            />

            <div>
              <div className="font-semibold text-white">
                @{name.toLowerCase()}
              </div>

              <div className="text-xs text-zinc-400">
                {niche}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 text-xs text-zinc-300">
            <Star
              className="w-3.5 h-3.5 fill-current"
              style={{ color: GREEN }}
            />
            4.9
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="text-zinc-400">
            For{" "}
            <span className="text-white font-semibold">
              {rate}
            </span>
          </span>

          <span className="text-zinc-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            48h
          </span>
        </div>
      </div>
    </div>
  );
}

function ContentTypes() {
  const types = [
    {
      icon: <Twitch className="w-6 h-6" />,
      label: "Twitch Highlights",
      tint: PURPLE,
      image: "https://static-cdn.jtvnw.net/twitch-video-assets/twitch-vap-video-assets-prod-us-west-2/4484f4fd-bd43-4ef3-b929-8c0acecbe5c5/landscape/thumb/thumb-0000000000-1920x1080.jpg"
    },
    {
      icon: <Youtube className="w-6 h-6" />,
      label: "YouTube Gaming Videos",
      tint: "#ef4444",
      image: "https://i3.ytimg.com/vi/Vyi_6o7ASCQ/maxresdefault.jpg"
    },
    {
      icon: <Music2 className="w-6 h-6" />,
      label: "TikTok / Reels / Shorts",
      tint: "#ec4899",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSz691B7xHDnQoA96Axde3suEJOTZMDhckHKA&s"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      label: "Stream Clips",
      tint: "#06b6d4",
      image: "https://i.ytimg.com/vi/Nk0F3uNuXTI/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLBkt2YMzaa9ebVuNcqA2ovbBKM_TQ"
    }
  ];

  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Every format"
          title="From Twitch clips to YouTube videos"
          sub="Use editors for deeper content and clippers for campaigns built around TikTok, Shorts, Reels, and stream growth."
        />

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-2 gap-4">
          {types.map((t) => (
            <div
              key={t.label}
              className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-[5/3] flex flex-col justify-end p-5 transition hover:-translate-y-1"
              style={{
                boxShadow: `0 0 40px ${t.tint}15`,
              }}
            >
              <img
                src={t.image}
                alt={t.label}
                className="absolute inset-0 w-full h-full object-cover"
              />

              <div className="absolute inset-0 bg-black/45" />

              {/* Gradient overlay ON TOP of image */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(160deg, ${t.tint}55, #0a0a14cc 70%)`,
                }}
              />
              <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300"
                style={{
                  background: `radial-gradient(60% 60% at 50% 0%, ${t.tint}66, transparent)`,
                }}
              />

              <div className="relative z-10">
                <div
                  className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3 backdrop-blur-md"
                  style={{
                    background: `${t.tint}25`,
                    color: t.tint,
                    border: `1px solid ${t.tint}55`,
                  }}
                >
                  {t.icon}
                </div>

                <div className="text-lg font-semibold text-white drop-shadow-lg">
                  {t.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "01",
      t: "Find your match",
      d: "Browse editors by niche, or get ready to open a clipping campaign.",
    },
    {
      n: "02",
      t: "Share footage and direction",
      d: "Share streams, clips, references, and the kind of viewer you want to reach.",
    },
    {
      n: "03",
      t: "Collaborate directly",
      d: "Chat, review work, and keep your content moving without random DMs.",
    },
    {
      n: "04",
      t: "Publish and grow",
      d: "Turn more stream moments into content that can bring people back to your channel.",
    },
  ];

  return (
    <section id="how" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="How it works"
          title="From stream moment to published content"
          sub="A simple flow for custom editing work and campaign driven clips."
        />

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5 relative">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className="relative rounded-2xl p-6 border border-white/10 backdrop-blur-xl"
              style={{
                background:
                  "linear-gradient(160deg, rgba(145,70,255,0.08), rgba(255,255,255,0.02))",
              }}
            >
              <div
                className="text-sm font-mono font-bold mb-4"
                style={{
                  color: i % 2 === 0 ? GREEN : PURPLE,
                }}
              >
                {s.n}
              </div>

              <h3 className="text-lg font-semibold text-white">
                {s.t}
              </h3>

              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Rewards() {
  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <SectionHeader
            align="left"
            eyebrow="Rewards & payouts"
            title="A platform that pays you back"
            sub="Earn points for completed work. Editors and clippers can build stronger profiles, while creators get perks that help them keep content moving."
          />

          <ul className="mt-6 space-y-3 text-sm text-zinc-300">
            {[
              "Reward points on every completed project",
              "Direct, instant payouts to editors",
              "Creator perks: discounts, priority editors, featured spots",
              "No platform fee gouging, keep more of what you earn",
            ].map((t) => (
              <li
                key={t}
                className="flex items-start gap-3"
              >
                <Check
                  className="w-4 h-4 mt-0.5"
                  style={{ color: GREEN }}
                />
                {t}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-black"
              style={{
                background: GREEN,
                boxShadow: `0 10px 40px ${GREEN}40`,
              }}
              onClick={() =>
                trackEvent("signup_click", {
                  location: "rewards",
                })
              }
            >
              Start earning
            </Link>
          </div>
        </div>

        <div className="lg:col-span-6">
          <RewardsVisual />
        </div>
      </div>
    </section>
  );
}

function RewardsVisual() {
  return (
    <div className="relative">
      <div
        className="rounded-3xl p-8 border border-white/10 backdrop-blur-xl"
        style={{
          background: `linear-gradient(160deg, ${PURPLE}33, rgba(0,0,0,0.4))`,
          boxShadow: `0 30px 80px rgba(0,0,0,0.5), 0 0 80px ${PURPLE}25`,
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-zinc-400">
              Kre8 Wallet
            </div>

            <div className="mt-1 text-3xl font-bold text-white">
              $4,820.50
            </div>
          </div>

          <div
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{
              background: `${GREEN}25`,
              color: GREEN,
            }}
          >
            <Sparkles className="w-3 h-3" />
            +1,240 pts
          </div>
        </div>

        <div
          className="mt-6 h-2 rounded-full overflow-hidden"
          style={{
            background: "rgba(255,255,255,0.06)",
          }}
        >
          <div
            className="h-full"
            style={{
              width: "78%",
              background: `linear-gradient(90deg, ${PURPLE}, ${GREEN})`,
            }}
          />
        </div>

        <div className="mt-2 text-xs text-zinc-400">
          78% to next tier · Pro Editor
        </div>

        <div className="mt-6 space-y-3">
          {[
            {
              t: "Twitch Highlight Reel",
              a: "+$100",
              d: "2h ago",
            },
            {
              t: "YouTube Long Form Edit",
              a: "+$110",
              d: "yesterday",
            },
            {
              t: "TikTok Series · 6 clips",
              a: "+55",
              d: "2d ago",
            },
          ].map((r) => (
            <div
              key={r.t}
              className="flex items-center justify-between rounded-xl px-4 py-3 border border-white/5"
              style={{
                background: "rgba(255,255,255,0.03)",
              }}
            >
              <div>
                <div className="text-sm text-white">
                  {r.t}
                </div>

                <div className="text-xs text-zinc-500">
                  {r.d}
                </div>
              </div>

              <div
                className="text-sm font-semibold"
                style={{ color: GREEN }}
              >
                {r.a}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Compare() {
  const rows = [
    {
      l: "Niche specialists for your content",
      k: true,
      t: false,
    },
    {
      l: "Direct creator and editor communication",
      k: true,
      t: false,
    },
    {
      l: "Built for long term partnerships",
      k: true,
      t: false,
    },
    {
      l: "No bidding wars or race to the bottom",
      k: true,
      t: false,
    },
    {
      l: "Rewards & loyalty payouts",
      k: true,
      t: false,
    },
    {
      l: "Generic freelance gigs",
      k: false,
      t: true,
    },
    {
      l: "20%+ platform fees",
      k: false,
      t: true,
    },
  ];

  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="The difference"
          title="Why creators, editors, and clippers choose Kre8"
          sub="A creator economy network built around Twitch content, not another generic freelance marketplace."
        />

        <div className="mt-14 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <CompareCol
            title="Kre8"
            highlight
            items={rows.filter((r) => r.k).map((r) => r.l)}
          />

          <CompareCol
            title="Fiverr / Upwork"
            negative
            items={rows.filter((r) => r.t).map((r) => r.l)}
          />
        </div>
      </div>
    </section>
  );
}

function CompareCol({
  title,
  items,
  highlight,
  negative,
}) {
  return (
    <div
      className="rounded-3xl p-7 border backdrop-blur-xl"
      style={{
        borderColor: highlight
          ? `${GREEN}55`
          : "rgba(255,255,255,0.08)",

        background: highlight
          ? `linear-gradient(160deg, ${GREEN}10, rgba(145,70,255,0.08))`
          : "rgba(255,255,255,0.02)",

        boxShadow: highlight
          ? `0 20px 60px ${GREEN}15`
          : "none",
      }}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-white">
          {title}
        </h3>

        {highlight && (
          <span
            className="text-[10px] px-2 py-1 rounded-full font-semibold"
            style={{
              background: `${GREEN}25`,
              color: GREEN,
            }}
          >
            RECOMMENDED
          </span>
        )}
      </div>

      <ul className="mt-5 space-y-3 text-sm text-zinc-300">
        {items.map((t) => (
          <li
            key={t}
            className="flex items-start gap-3"
          >
            {negative ? (
              <X className="w-4 h-4 mt-0.5 text-zinc-500" />
            ) : (
              <Check
                className="w-4 h-4 mt-0.5"
                style={{ color: GREEN }}
              />
            )}

            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FAQ() {
  const faqs = [
    {
      q: "Is Kre8 for creators, editors, or both?",
      a: "Both. Creators come to find Twitch editors and clippers. Editors and clippers come to build profiles around the creator work they actually want.",
    },
    {
      q: "Where do clipping campaigns fit?",
      a: "Editors are for custom video work. Clippers are for campaign driven short form content from Twitch streams, built for TikTok, Shorts, and Reels.",
    },
    {
      q: "Are there hidden fees?",
      a: "No. We don't take 20%+ like generic freelance marketplaces. Editors keep more of what they earn, and creators see transparent pricing up front.",
    },
    {
      q: "Can clippers join before campaigns are fully open?",
      a: "Yes. Clippers can create a profile now and get early access while Kre8 opens clipping campaigns to more streamers.",
    },
    {
      q: "How do I join as an editor or clipper?",
      a: "Create an account, build out your profile, and make your style clear. Clippers should show the stream moments, games, and short form formats they know best.",
    }, {
      q: "If I need help, how do I reach out?",
      a: "You can email me personally at anteos1@kre8content.com, the support team, support@kre8content.com or just send a message in Discord!",
    }, {
      q: "How do payouts work?",
      a: "For editors, you signup for an account with Stripe, choose your payment method and withdraw. For creators, you'll recieve your cash redemptions via PayPal.",
    },
  ];

  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="py-24">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="FAQ"
          title="Questions, answered"
        />

        <div className="mt-12 space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;

            return (
              <div
                key={i}
                className="rounded-2xl border border-white/10 backdrop-blur-xl overflow-hidden"
                style={{
                  background: "rgba(255,255,255,0.025)",
                }}
              >
                <button
                  onClick={() =>
                    setOpen(isOpen ? null : i)
                  }
                  className="w-full flex items-center justify-between gap-4 text-left p-5 cursor-pointer"
                >
                  <span className="font-semibold text-white">
                    {f.q}
                  </span>

                  <ChevronDown
                    className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>

                <div
                  className="grid transition-all duration-300"
                  style={{
                    gridTemplateRows: isOpen
                      ? "1fr"
                      : "0fr",
                  }}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 text-sm text-zinc-400 leading-relaxed">
                      {f.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div
          className="relative rounded-3xl p-10 sm:p-16 border border-white/10 overflow-hidden text-center"
          style={{
            background: `linear-gradient(135deg, ${PURPLE}55, #0a0a14 50%, ${GREEN}33)`,
            boxShadow: `0 30px 100px rgba(0,0,0,0.6), 0 0 100px ${PURPLE}33`,
          }}
        >
          <div
            aria-hidden
            className="absolute -top-32 left-1/4 w-72 h-72 rounded-full blur-3xl opacity-50"
            style={{ background: PURPLE }}
          />

          <div
            aria-hidden
            className="absolute -bottom-32 right-1/4 w-72 h-72 rounded-full blur-3xl opacity-40"
            style={{ background: GREEN }}
          />

          <div className="relative">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border mb-6"
              style={{
                borderColor: `${GREEN}55`,
                background: `${GREEN}10`,
                color: GREEN,
              }}
            >
              <Sparkles className="w-3 h-3" />
              Grow faster together
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl mx-auto leading-[1.05]">
              Find your next{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(120deg, ${GREEN}, ${PURPLE})`,
                }}
              >
                creative partner
              </span>
            </h2>

            <p className="mt-5 text-zinc-300 max-w-xl mx-auto">
              Whether you need more content or want early access to creator
              campaigns, start with Kre8 today.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/gaming-video-editors"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-white"
                style={{
                  background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})`,
                  boxShadow: `0 10px 40px ${PURPLE}66`,
                }}
                onClick={() =>
                trackEvent("find_editor_click", {
                  location: "bottomCTA",
                })
              }
              >
                Browse Editors
                <ArrowRight className="w-4 h-4" />
              </Link>

              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-black"
                style={{
                  background: GREEN,
                  boxShadow: `0 10px 40px ${GREEN}40`,
                }}
                onClick={() =>
                trackEvent("signup_click", {
                  location: "bottomCTA",
                })
              }
              >
                Start Growing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({
  eyebrow,
  title,
  sub,
  align = "center",
}) {
  const a =
    align === "center"
      ? "text-center mx-auto"
      : "text-left";

  return (
    <div className={`${a} max-w-2xl`}>
      <div
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
        style={{
          background: `${PURPLE}20`,
          color: "#c8b3ff",
          border: `1px solid ${PURPLE}40`,
        }}
      >
        {eyebrow}
      </div>

      <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        {title}
      </h2>

      {sub && (
        <p className="mt-4 text-zinc-400 leading-relaxed">
          {sub}
        </p>
      )}
    </div>
  );
}
